import { definePreset } from '@primeng/themes';
import Aura from '@primeng/themes/aura';

export const Noir = definePreset(Aura, {
  semantic: {
    // Zinc palette
    primary: {
      50: '{zinc.50}',
      100: '{zinc.100}',
      200: '{zinc.200}',
      300: '{zinc.300}',
      400: '{zinc.400}',
      500: '{zinc.500}',
      600: '{zinc.600}',
      700: '{zinc.700}',
      800: '{zinc.800}',
      900: '{zinc.900}',
      950: '{zinc.950}',
    },
    // CXC (Purple / Murrey) palette
    secondary: {
      50: '#FFF5FB',
      100: '#FFEBF8',
      200: '#FCD4EF',
      300: '#FDB0E4',
      400: '#E670BE',
      500: '#C92693',
      600: '#9C1676',
      700: '#840058',
      800: '#57003A',
      900: '#330022',
      950: '#1A0012',
    },
    // Slate palette
    surface: {
      0: '#FFFFFF',
      50: '#f8fafc',
      100: '#f1f5f9',
      150: '#eaeff5',
      200: '#e2e8f0',
      250: '#d7dfe9',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
      950: '#020617',
      1000: '#000000',
    },
    colorScheme: {
      light: {
        primary: {
          color: '{zinc.800}',
          inverseColor: '#ffffff',
          hoverColor: '{zinc.700}',
          activeColor: '{zinc.600}',
        },
        highlight: {
          background: '{zinc.800}',
          focusBackground: '{zinc.700}',
          color: '#ffffff',
          focusColor: '#ffffff',
        },
      },
      dark: {
        primary: {
          color: '{zinc.50}',
          inverseColor: '{zinc.800}',
          hoverColor: '{zinc.100}',
          activeColor: '{zinc.200}',
        },
        highlight: {
          background: 'rgba(250, 250, 250, .16)',
          focusBackground: 'rgba(250, 250, 250, .24)',
          color: 'rgba(255,255,255,.87)',
          focusColor: 'rgba(255,255,255,.87)',
        },
      },
    },
  },
  options: {
    cssLayer: {
      name: 'primeng',
      order: 'tailwind-base, primeng, tailwind-utilities',
    },
  },
  extends: {},
  components: {
    accordion: {
      colorScheme: {
        light: {
          gap: '4px',
          panel: {
            border: {
              width: '0px',
            },
          },
          header: {
            background: '{surface.50}',
            border: {
              width: '1px',
              radius: '6px',
              color: '{surface.300}',
            },
            top: {
              border: {
                radius: '6px',
              },
            },
            bottom: {
              border: {
                radius: '0px',
              },
            },
            hover: {
              background: '{surface.100}',
            },
            active: {
              background: '{surface.50}',
              hover: {
                background: '{surface.100}',
              },
            },
          },
          content: {
            border: {
              width: '1px',
              color: '{surface.300}',
            },
            padding: '32px',
            top: {
              border: {
                radius: '0px',
              },
            },
            bottom: {
              border: {
                radius: '6px',
              },
            },
          },
        },
      }
    },

    datepicker: {
      colorScheme: {
        light: {
          today: {
            background: '{surface.200}',
            color: '{surface.500}',
          },
          date: {
            color: '{surface.500}',
            border: { radius: '9999px' },
            selected: {
              background: '{primary.900}',
            },
            hover: {
              background: '{surface.200}',
              border: {
                width: '2px',
                color: '{surface.500}',
              },
            },
            range: {
              selected: {
                color: '{surface.500}',
                background: '{surface.100}',
              },
            },
          },
          invalid: {
            border: {
              color: '{red.500}',
            },
            shadow: '0 0 0 3px {red.100}',
          },
        },
      }
    },

    inputotp: {
      colorScheme: {
        light: {
          input: {
            invalid: {
              border: {
                color: '{red.500}',
                hover: {
                  color: '{red.500}',
                },
              },
            },
          },
        },
      }
    },

    inputnumber: {
      colorScheme: {
        light: {
          invalid: {
            shadow: '0 0 0 3px {red.100}',
          }
        }
      }
    },

    inputtext: {
      colorScheme: {
        light: {
          invalid: {
            shadow: '0 0 0 3px {red.100}',
          },
          focus: {
            shadow: '0 0 0 3px {primary.300}',
            border: {
              color: '{surface.900}',
            }
          },
        },
      },
    },

    textarea: {
      colorScheme: {
        light: {
          invalid: {
            ring: {
              shadow: '0 0 0 3px {red.100}',
            }
          },
          focus: {
            ring: {
              shadow: '0 0 0 3px {primary.300}',
            },
            border: {
              color: '{surface.900}',
            }
          },
        },
      },
    },

    datatable: {
      colorScheme: {
        light: {
          border: {
            radius: '12px',
            width: '1px',
            color: '{surface.300}',
          },
          header: {
            cell: {
              background: '{slate.50}',
            },
          },
        },
      },
    },

    password: {
      colorScheme: {
        light: {
          invalid: {
            border: {
              color: '{red.500}',
            },
            shadow: '0 0 0 3px {red.100}',
          },
        },
      },
    },

    checkbox: {
      colorScheme: {
        light: {
          invalid: {
            border: {
              color: '{red.500}',
            },
            shadow: '0 0 0 3px {red.100}',
          },
        },
      },
    },

    badge: {
      colorScheme: {
        light: {
          primary: { background: '{secondary.500}' },
        },
      },
    },

    menu: {
      colorScheme: {
        light: {
          border: { color: '{surface.300}' },
          shadow: '0 4px 6px -1px {surface.200}, 0 2px 4px -2px {surface.200};',
        },
      },
    },

    dialog: {
      colorScheme: {
        light: {
          content: {
            padding: '0px',
          },
        },
      },
    },

    chip: {
      colorScheme: {
        light: {
          padding: {
            x: '8px',
            y: '4px',
          },
          gap: '8px',
        },
      },
    },

    button: {
      colorScheme: {
        light: {
          // padding: {
          //   y: '4px'
          // },
          secondary: {
            background: '{surface.100}',
            border: {
              color: '{surface.100}',
            },
            hover: {
              background: '{surface.200}',
              border: {
                color: '{surface.200}',
              },
            },
            active: {
              background: '{surface.300}',
              border: {
                color: '{surface.300}',
              },
            },
          },
        },
      },
    },

    select: {
      colorScheme: {
        light: {
          invalid: {
              ring: {
                shadow: '0 0 0 3px {red.100}',
              }
            },
            focus: {
              background: '{surface.250}',
              ring: {
                shadow: '0 0 0 3px {primary.300}',
              }
            },
          option: {
            selected: {
              color: '{surface.900}',
              background: '{surface.150}',
              focus: {
                color: '{surface.900}',
                background: '{surface.300}',
              },
            },
          },
        },
      },
    },

    multiselect: {
      colorScheme: {
        light: {
          invalid: {
            ring: {
              shadow: '0 0 0 3px {red.100}',
            }
          },
          focus: {
            background: '{surface.250}',
            ring: {
              shadow: '0 0 0 3px {primary.300}',
            }
          },
          option: {
            selected: {
              color: '{surface.900}',
              background: '{surface.150}',
              focus: {
                color: '{surface.900}',
                background: '{surface.300}',
              },
            },
          }
        },
      },
    },
  },
});
